import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Tab, Tabs, Table, Badge, ProgressBar, Alert, Spinner, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { db, functions } from '../firebase';
import { collection, getDocs, getCountFromServer, query, where, orderBy, limit } from 'firebase/firestore';
import { devLog, devError } from '../utils/devLogger';
import { httpsCallable } from 'firebase/functions';
import AdminNavbar from '../components/AdminNavbar';
import SessionRSVPAdmin from '../components/SessionRSVPAdmin';
import SessionPollAdmin from '../components/SessionPollAdmin';
import { DOCUMENT_CATEGORIES, checkDocumentDeadline } from '../config/documentCategories';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import '../styles/brand.css';
import { isRealUser } from '../utils/userFilters';

const AdminDashboard = () => {
  usePageTitle("Admin Dashboard");
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState({
    totalLearners: 0,
    activeLearners: 0,
    inactiveLearners: 0,
    totalFacilitators: 0,
    totalModules: 0,
    completionRate: 0,
    avgStudyTime: 0,
    openIssues: 0,
    highPriorityIssues: 0,
    attendanceRate: 0,
    totalEnrollments: 0,
    totalCompletions: 0,
    documentsSubmitted: 0,
    documentsOutstanding: 0,
    nonCompliantDocuments: 0,
    onTimeSubmissionRate: 0,
    outstandingProgressReports: 0,
    overallAttendance: 0,
    outstandingAttendanceRegisters: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvince, setSelectedProvince] = useState('all');
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [documentStats, setDocumentStats] = useState({});
  const [cohorts, setCohorts] = useState([]);
  const [collectionsData, setCollectionsData] = useState(null);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [signedRegisters, setSignedRegisters] = useState([]);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (currentUser) {
      loadUsersAndCalculateAnalytics();
      loadCohorts();
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    calculateAnalytics(users, collectionsData);
  }, [selectedProvince, selectedCohort, users, collectionsData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Run backfill to sync all documents to QA collection
  const runDocumentBackfill = async () => {
    try {
      setBackfillRunning(true);
      setBackfillResult(null);

      const backfillFunction = httpsCallable(functions, 'backfillDocumentsToQA');
      const result = await backfillFunction({});

      setBackfillResult(result.data);
      alert(`Success. Synced ${result.data.stats.syncedDocuments} documents to QA Dashboard.`);
    } catch (error) {
      devError('Backfill error:', error.message);
      alert('Error running backfill: ' + error.message);
      setBackfillResult({ error: error.message });
    } finally {
      setBackfillRunning(false);
    }
  };

  // Run diagnostic to check document structure
  const runDiagnostic = async () => {
    try {
      const diagnosticFunction = httpsCallable(functions, 'checkDocumentStructure');
      const result = await diagnosticFunction({});

      devLog('Diagnostic results:', result.data);

      setDiagnosticResult(result.data);
      alert('Diagnostic complete. Check browser console for details.');
    } catch (error) {
      devError('Diagnostic error:', error.message);
      alert('Error running diagnostic: ' + error.message);
    }
  };

  const exportSignedRegisterPdf = (sessionId) => {
    const session = sessions.find((item) => item.id === sessionId);
    const signedSession = signedRegisters.find((item) => item.sessionId === sessionId);
    if (!signedSession) return;

    const usersById = new Map(users.map((user) => [user.id, user]));
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Signed Attendance Register', 14, 18);
    doc.setFontSize(11);
    doc.setTextColor(80);
    doc.text(`Session: ${signedSession.title}`, 14, 26);
    doc.text(`Cohort: ${signedSession.cohortName}`, 14, 32);
    doc.text(`Date: ${signedSession.scheduledDate}`, 14, 38);
    doc.text(`Confirmed: ${signedSession.confirmedCount}`, 14, 44);

    const rows = signedSession.records.map((record, index) => {
      const learner = usersById.get(record.learnerId) || {};
      const markedAtValue = record.markedAt?.toDate
        ? record.markedAt.toDate().toLocaleString()
        : (record.markedAt ? new Date(record.markedAt).toLocaleString() : 'N/A');
      return [
        String(index + 1),
        learner.displayName || learner.email || 'Unknown',
        learner.email || '',
        markedAtValue
      ];
    });

    autoTable(doc, {
      head: [['#', 'Learner', 'Email', 'Confirmed At']],
      body: rows,
      startY: 52
    });

    const safeTitle = (session?.title || session?.moduleName || 'session').replace(/[^a-z0-9]+/gi, '_');
    doc.save(`signed_register_${safeTitle}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Load all collections data from Firebase for admin dashboard
  const loadUsersAndCalculateAnalytics = async () => {
    try {
      setLoading(true);
      // Full document reads (needed for user filtering, signed registers, PDF export)
      // Count-only collections use getCountFromServer — ~99% fewer Firestore reads
      const [
        usersSnapshot,
        programsSnapshot,
        cohortsSnapshot,
        attendanceSnapshot,
        sessionsSnapshot,
        attendanceTotalResult,
        modulesTotalResult,
        modulesCompletedResult,
        quizzesTotalResult,
        openIssuesResult,
        highPriorityIssuesResult,
        assignmentsTotalResult,
      ] = await Promise.all([
        getDocs(query(collection(db, 'users'), limit(500))),
        getDocs(query(collection(db, 'programs'), limit(200))),
        getDocs(query(collection(db, 'cohorts'), limit(200))),
        // Fetch only self-marked attendance records (needed for signed register display) — bounded
        getDocs(query(collection(db, 'attendance'), where('source', '==', 'learner-self-marked'), orderBy('createdAt', 'desc'), limit(500))),
        // Fetch only the 100 most recent sessions — bounded
        getDocs(query(collection(db, 'sessions'), orderBy('scheduledDate', 'desc'), limit(100))),
        // Use count aggregation for the total attendance stat — zero document transfer
        getCountFromServer(collection(db, 'attendance')),
        getCountFromServer(collection(db, 'modules')),
        getCountFromServer(query(collection(db, 'modules'), where('status', '==', 'completed'))),
        getCountFromServer(collection(db, 'quizzes')),
        getCountFromServer(query(collection(db, 'issues'), where('status', '==', 'open'))),
        getCountFromServer(query(collection(db, 'issues'), where('priority', '==', 'high'))),
        getCountFromServer(collection(db, 'assignments')),
      ]);

      // Map snapshots to arrays
      const usersList = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(isRealUser);
      const programsList = programsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const cohortsList = cohortsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const attendanceList = attendanceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const sessionsList = sessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledDate: doc.data().scheduledDate?.toDate?.() || null
      }));

      // Extract count values
      const attendanceCount = attendanceTotalResult.data().count;
      const modulesCount = modulesTotalResult.data().count;
      const completedModulesCount = modulesCompletedResult.data().count;
      const quizzesCount = quizzesTotalResult.data().count;
      const openIssuesCount = openIssuesResult.data().count;
      const highPriorityIssuesCount = highPriorityIssuesResult.data().count;
      const assignmentsCount = assignmentsTotalResult.data().count;

      devLog('Loaded Firebase Collections:', {
        users: usersList.length,
        programs: programsList.length,
        cohorts: cohortsList.length,
        attendance: attendanceCount,
        selfMarkedAttendanceRecords: attendanceList.length,
        sessions: sessionsList.length,
        modules: modulesCount,
        completedModules: completedModulesCount,
        quizzes: quizzesCount,
        openIssues: openIssuesCount,
        highPriorityIssues: highPriorityIssuesCount,
        assignments: assignmentsCount,
      });

      // Store data for analytics (counts stored as numbers via aggregation queries)
      const collectionsDataObject = {
        programs: programsList,
        cohorts: cohortsList,
        // attendance is the server-side total count — the filtered list is only for signed registers
        attendance: { length: attendanceCount },
        sessions: sessionsList,
        modulesCount,
        completedModulesCount,
        quizzesCount,
        openIssuesCount,
        highPriorityIssuesCount,
        assignmentsCount,
      };

      setUsers(usersList);
      setCohorts(cohortsList);
      setSessions(sessionsList);
      setCollectionsData(collectionsDataObject);

      // attendanceList is already filtered to source === 'learner-self-marked' by the Firestore query
      const signedBySession = attendanceList
        .reduce((acc, record) => {
          const sessionId = record.sessionId || 'unknown';
          if (!acc[sessionId]) {
            acc[sessionId] = [];
          }
          acc[sessionId].push(record);
          return acc;
        }, {});

      const signedRegistersList = Object.entries(signedBySession).map(([sessionId, records]) => {
        const session = sessionsList.find((item) => item.id === sessionId);
        return {
          sessionId,
          title: session?.title || session?.moduleName || 'Session',
          cohortName: session?.cohortName || 'Cohort',
          scheduledDate: session?.scheduledDate
            ? session.scheduledDate.toLocaleDateString()
            : 'Unknown',
          confirmedCount: records.length,
          records
        };
      }).sort((a, b) => a.cohortName.localeCompare(b.cohortName));

      setSignedRegisters(signedRegistersList);

      // Calculate analytics with all data
      calculateAnalytics(usersList, collectionsDataObject);
      generateRecentActivity(usersList);
    } catch (error) {
      devError('Error loading Firebase data:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = (usersList = users, collectionsData = null) => {

    const learners = usersList.filter(user => user.role === 'learner');
    const facilitators = usersList.filter(user => user.role === 'facilitator');
    const admins = usersList.filter(user => user.role === 'admin');

    // Handle both isActive boolean and status string for active/inactive logic
    // Default to active if no status is specified
    const activeLearners = learners.filter(user => {
      // If user has explicit status, use that
      if (user.status) {
        return user.status === 'active';
      }
      // If user has isActive boolean, use that
      if (user.hasOwnProperty('isActive')) {
        return user.isActive === true;
      }
      // Default to active if no status specified
      return true;
    });

    const inactiveLearners = learners.filter(user => {
      // If user has explicit status, use that
      if (user.status) {
        return user.status !== 'active';
      }
      // If user has isActive boolean, use that
      if (user.hasOwnProperty('isActive')) {
        return user.isActive === false;
      }
      // Default to active (so this would be false for inactive)
      return false;
    });

    // Filter learners by province and cohort for detailed analytics
    let filteredLearners = learners;
    if (selectedProvince !== 'all') {
      filteredLearners = filteredLearners.filter(user => user.province === selectedProvince);
    }
    if (selectedCohort !== 'all') {
      filteredLearners = filteredLearners.filter(user => user.cohortId === selectedCohort);
    }

    // Calculate document submission statistics (simplified)
    const documentStats = calculateDocumentStats(filteredLearners);

    // Calculate real statistics using collections data
    const totalEnrollments = learners.length; // Use learner count as enrollment count
    let completionRate = 0;
    let avgStudyTime = 0;
    let totalPrograms = 0;
    let totalModules = 0;
    let totalCohorts = 0;
    let totalAssessments = 0;
    let openIssues = 0;
    let attendanceRecords = 0;
    let assignments = 0;

    // Use collections data if available
    if (collectionsData) {
      totalPrograms = collectionsData.programs?.length || 0;
      totalModules = collectionsData.modulesCount || 0;
      totalCohorts = collectionsData.cohorts?.filter(c => c.isActive !== false)?.length || collectionsData.cohorts?.length || 0;
      totalAssessments = collectionsData.quizzesCount || 0;
      openIssues = collectionsData.openIssuesCount || 0;
      attendanceRecords = collectionsData.attendance?.length || 0;
      assignments = collectionsData.assignmentsCount || 0;

      // Calculate completion rate using pre-fetched server-side counts
      const totalCompletions = collectionsData.completedModulesCount || 0;
      if (totalModules > 0 && learners.length > 0) {
        completionRate = Math.round((totalCompletions / (totalModules * learners.length)) * 100);
      }
    }

    setAnalytics({
      // User statistics - show total counts, not filtered
      totalLearners: learners?.length || 0,
      activeLearners: activeLearners?.length || 0,
      inactiveLearners: inactiveLearners?.length || 0,
      totalFacilitators: facilitators?.length || 0,
      totalAdmins: admins?.length || 0,

      // Content statistics (now with real data)
      totalModules,
      totalPrograms,
      totalCohorts,
      totalAssessments,

      // Learning statistics (enhanced)
      totalEnrollments: totalEnrollments || 0,
      totalCompletions: 0,
      completionRate: Math.round(completionRate),
      avgStudyTime: Math.round(avgStudyTime),

      // Communication statistics (simplified)
      totalMessages: 0,
      totalNotifications: 0,
      unreadNotifications: 0,

      // System health (now with real data)
      openIssues,
      highPriorityIssues: collectionsData?.highPriorityIssuesCount || 0,

      // Document compliance
      documentsSubmitted: documentStats.submitted,
      documentsOutstanding: documentStats.outstanding,
      nonCompliantDocuments: documentStats.nonCompliant,
      onTimeSubmissionRate: documentStats.onTimeRate,

      // Progress and attendance (enhanced with real data)
      outstandingProgressReports: 0,
      overallAttendance: attendanceRecords > 0 ? Math.round((attendanceRecords / (learners.length * 30)) * 100) : 0, // Rough estimate
      outstandingAttendanceRegisters: 0,
      totalAttendanceRecords: attendanceRecords,
      totalAssignments: assignments
    });

    setDocumentStats(documentStats);
  };

  // Simplified helper functions (replaced complex versions that required other collections)

  const getEffectiveDocumentStatus = (document = {}) => {
    if (document.reviewStatus === 'qa_approved') return 'verified';
    if (document.reviewStatus === 'qa_rejected') return 'rejected';
    if (document.status === 'approved') return 'verified';
    return document.status || 'pending';
  };

  const getRequiredDocumentKeysForLearner = (learner) => {
    const learnerProgram = collectionsData?.programs?.find((program) => program.id === learner?.programId);
    const configured = Array.isArray(learnerProgram?.requiredDocuments)
      ? learnerProgram.requiredDocuments.filter(Boolean)
      : [];

    if (configured.length > 0) {
      return configured;
    }

    return Object.keys(DOCUMENT_CATEGORIES).filter(
      key => DOCUMENT_CATEGORIES[key].mandatory
    );
  };


  const calculateDocumentStats = (learners) => {
    let totalRequired = 0;
    let totalSubmitted = 0;
    let totalNonCompliant = 0;
    let onTimeLearners = 0;
    const provinceStats = {};
    const cohortStats = {};

    learners.forEach(learner => {
      const learnerDocuments = learner.documents || {};
      const registrationDate = learner.createdAt?.toDate ? learner.createdAt.toDate() : new Date(learner.createdAt || Date.now());
      const deadlineInfo = checkDocumentDeadline(registrationDate);
      const requiredDocuments = getRequiredDocumentKeysForLearner(learner);

      let learnerSubmitted = 0;
      let learnerNonCompliant = 0;
      let learnerRequired = requiredDocuments.length;
      let isOnTime = true;

      requiredDocuments.forEach(docCategory => {
        const document = learnerDocuments[docCategory];
        if (document && document.uploadDate) {
          learnerSubmitted++;
          // Check if document is rejected (non-compliant)
          if (getEffectiveDocumentStatus(document) === 'rejected') {
            learnerNonCompliant++;
          }
        } else if (deadlineInfo.isOverdue) {
          isOnTime = false;
        }
      });

      if (isOnTime && learnerSubmitted === learnerRequired) {
        onTimeLearners++;
      }

      totalRequired += learnerRequired;
      totalSubmitted += learnerSubmitted;
      totalNonCompliant += learnerNonCompliant;

      // Province stats
      const province = learner.province || 'Unknown';
      if (!provinceStats[province]) {
        provinceStats[province] = { submitted: 0, outstanding: 0, nonCompliant: 0, total: 0 };
      }
      provinceStats[province].submitted += learnerSubmitted;
      provinceStats[province].outstanding += (learnerRequired - learnerSubmitted);
      provinceStats[province].nonCompliant += learnerNonCompliant;
      provinceStats[province].total++;

      // Cohort stats
      const cohortId = learner.cohortId || 'Unknown';
      if (!cohortStats[cohortId]) {
        cohortStats[cohortId] = { submitted: 0, outstanding: 0, nonCompliant: 0, total: 0 };
      }
      cohortStats[cohortId].submitted += learnerSubmitted;
      cohortStats[cohortId].outstanding += (learnerRequired - learnerSubmitted);
      cohortStats[cohortId].nonCompliant += learnerNonCompliant;
      cohortStats[cohortId].total++;
    });

    return {
      submitted: totalSubmitted,
      outstanding: totalRequired - totalSubmitted,
      nonCompliant: totalNonCompliant,
      onTimeRate: learners.length > 0 ? Math.round((onTimeLearners / learners.length) * 100) : 0,
      provinceStats,
      cohortStats
    };
  };

  const loadCohorts = async () => {
    try {
      const cohortsCollection = collection(db, 'cohorts');
      const snapshot = await getDocs(cohortsCollection);
      const cohortsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCohorts(cohortsList);
    } catch (error) {
      devError('Error loading cohorts:', error.message);
    }
  };

  const getUniqueProvinces = () => {
    const provinces = [...new Set(users.filter(user => user.role === 'learner' && user.province).map(user => user.province))];
    return provinces.sort();
  };

  const generateRecentActivity = (usersList = users) => {
    const activities = [];
    
    // Add recent user registrations
    usersList
      .filter(user => user.createdAt)
      .sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      })
      .slice(0, 5)
      .forEach(user => {
        const createdDate = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
        activities.push({
          type: 'registration',
          title: `New ${user.role} registered`,
          user: user.displayName,
          time: createdDate.toLocaleString(),
          priority: 'normal'
        });
      });
    
    setRecentActivity(activities);
  };


  const getActivityIcon = (type) => {
    switch (type) {
      case 'issue': return '!';
      case 'completion': return 'OK';
      case 'enrollment': return 'E';
      default: return 'i';
    }
  };

  if (userProfile?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access the admin dashboard.</p>
          <Button variant="primary" onClick={() => {
            if (userProfile?.role === 'facilitator') {
              navigate('/facilitator');
            } else {
              navigate('/dashboard');
            }
          }}>
            Go to Dashboard
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <AdminNavbar currentPage="dashboard" />

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Admin Dashboard</h1>
              <p className="mission-text mb-4">
                Manage users, learner progress, compliance, and system activity.
              </p>
              <p className="mb-0">
                Review progress, document status, attendance, and operational metrics from one place.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">

        {/* Key Metrics */}
        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading dashboard data...</p>
          </div>
        ) : (
        <>
        {/* Filters */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Filter by Province</Form.Label>
                      <Form.Select 
                        value={selectedProvince} 
                        onChange={(e) => setSelectedProvince(e.target.value)}
                      >
                        <option value="all">All Provinces</option>
                        {getUniqueProvinces().map(province => (
                          <option key={province} value={province}>{province}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Filter by Cohort</Form.Label>
                      <Form.Select 
                        value={selectedCohort} 
                        onChange={(e) => setSelectedCohort(e.target.value)}
                      >
                        <option value="all">All Cohorts</option>
                        {cohorts.map(cohort => (
                          <option key={cohort.id} value={cohort.id}>{cohort.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center dashboard-card" onClick={() => navigate('/admin/users')}>
              <Card.Body>
                <h3 className="text-primary">{analytics.totalLearners}</h3>
                <small className="text-muted">Total Learners</small>
                <div className="mt-2">
                  <Badge bg="success" className="me-1">{analytics.activeLearners} Active</Badge>
                  <Badge bg="secondary">{analytics.inactiveLearners} Inactive</Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center dashboard-card" onClick={() => navigate('/admin/compliance-reports')}>
              <Card.Body>
                <h3 className="text-success">
                  {analytics.documentsSubmitted + analytics.documentsOutstanding > 0
                    ? Math.round((analytics.documentsSubmitted / (analytics.documentsSubmitted + analytics.documentsOutstanding)) * 100)
                    : 0}%
                </h3>
                <small className="text-muted">Registration Document Submission Rate</small>
                <div className="mt-2">
                  <small className="text-success">{analytics.documentsSubmitted} Submitted</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center dashboard-card" onClick={() => navigate('/admin/compliance-reports')}>
              <Card.Body>
                <h3 className="text-warning">{analytics.documentsOutstanding}</h3>
                <small className="text-muted">Outstanding Documents</small>
                <div className="mt-2">
                  <small className="text-danger">{analytics.nonCompliantDocuments} Non-Compliant</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center dashboard-card" onClick={() => navigate('/admin/assessments')}>
              <Card.Body>
                <h3 className="text-info">{analytics.onTimeSubmissionRate}%</h3>
                <small className="text-muted">On-Time Submission Rate</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center dashboard-card" onClick={() => navigate('/admin/attendance')}>
              <Card.Body>
                <h3 className="text-primary">{analytics.overallAttendance}%</h3>
                <small className="text-muted">Overall Attendance</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center dashboard-card" onClick={() => navigate('/admin/attendance-reports')}>
              <Card.Body>
                <h3 className="text-warning">{analytics.outstandingAttendanceRegisters}</h3>
                <small className="text-muted">Outstanding Attendance Registers</small>
              </Card.Body>
            </Card>
            <Button
              variant="outline-primary"
              size="sm"
              className="w-100 mt-2"
              onClick={() => navigate('/admin/attendance')}
            >
              Prompt Attendance
            </Button>
          </Col>
          {userProfile?.role === 'admin' && (
            <Col md={3}>
              <Card className="text-center dashboard-card" onClick={() => navigate('/admin/progress-reports')}>
                <Card.Body>
                  <h3 className="text-danger">{analytics.outstandingProgressReports}</h3>
                  <small className="text-muted">Outstanding Progress Reports</small>
                </Card.Body>
              </Card>
            </Col>
          )}
          <Col md={3}>
            <Card className="text-center dashboard-card" onClick={() => navigate('/admin/issues')}>
              <Card.Body>
                <h3 className="text-secondary">{analytics.openIssues}</h3>
                <small className="text-muted">My Issues</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Additional System Metrics */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center dashboard-card" onClick={() => navigate('/admin/programs')}>
              <Card.Body>
                <h3 className="text-success">{analytics.totalPrograms}</h3>
                <small className="text-muted">Training Programs</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center dashboard-card" onClick={() => navigate('/admin/programs')}>
              <Card.Body>
                <h3 className="text-info">{analytics.totalModules}</h3>
                <small className="text-muted">Course Modules</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center dashboard-card" onClick={() => navigate('/admin/cohorts')}>
              <Card.Body>
                <h3 className="text-primary">{analytics.totalCohorts}</h3>
                <small className="text-muted">Active Cohorts</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center dashboard-card" onClick={() => navigate('/admin/assessments')}>
              <Card.Body>
                <h3 className="text-warning">{analytics.totalAssessments}</h3>
                <small className="text-muted">Assessments</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center dashboard-card" onClick={() => navigate('/admin/users')}>
              <Card.Body>
                <h3 className="text-primary">{analytics.totalEnrollments}</h3>
                <small className="text-muted">Total Enrollments</small>
                <div className="mt-2">
                  <small className="text-success">{analytics.totalCompletions} Completed</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center dashboard-card">
              <Card.Body>
                <h3 className="text-success">{analytics.completionRate}%</h3>
                <small className="text-muted">Completion Rate</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center dashboard-card" onClick={() => navigate('/admin/messages')}>
              <Card.Body>
                <h3 className="text-info">{analytics.totalMessages}</h3>
                <small className="text-muted">System Messages</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center dashboard-card" onClick={() => navigate('/admin/notifications')}>
              <Card.Body>
                <h3 className="text-warning">{analytics.totalNotifications}</h3>
                <small className="text-muted">Notifications</small>
                <div className="mt-2">
                  <small className="text-danger">{analytics.unreadNotifications} Unread</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center dashboard-card">
              <Card.Body>
                <h3 className="text-primary">{analytics.totalFacilitators}</h3>
                <small className="text-muted">Facilitators</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center dashboard-card">
              <Card.Body>
                <h3 className="text-secondary">{analytics.totalAdmins}</h3>
                <small className="text-muted">Administrators</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center dashboard-card">
              <Card.Body>
                <h3 className="text-info">{analytics.totalAttendanceRecords}</h3>
                <small className="text-muted">Attendance Records</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center dashboard-card">
              <Card.Body>
                <h3 className="text-warning">{analytics.totalAssignments}</h3>
                <small className="text-muted">Assignments</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Alerts */}
        <Row className="mb-4">
          <Col>
            {analytics.nonCompliantDocuments > 0 && (
              <Alert variant="danger" className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Compliance Alert:</strong> {analytics.nonCompliantDocuments} documents have been rejected and need resubmission
                </div>
                <Button variant="outline-danger" size="sm" onClick={() => navigate('/admin/reports/compliance?filter=rejected')}>
                  View Non-Compliant
                </Button>
              </Alert>
            )}
            {analytics.documentsOutstanding > 0 && (
              <Alert variant="warning" className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Document Status:</strong> {analytics.documentsOutstanding} documents still pending submission
                </div>
                <Button variant="outline-warning" size="sm" onClick={() => navigate('/admin/reports/compliance?filter=outstanding')}>
                  View Outstanding
                </Button>
              </Alert>
            )}
            {analytics.outstandingAttendanceRegisters > 0 && (
              <Alert variant="info" className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Attendance Update:</strong> {analytics.outstandingAttendanceRegisters} attendance registers need completion
                </div>
                <Button variant="outline-info" size="sm" onClick={() => navigate('/admin/reports/attendance?filter=incomplete')}>
                  Complete Registers
                </Button>
              </Alert>
            )}
            {userProfile?.role === 'admin' && analytics.outstandingProgressReports > 0 && (
              <Alert variant="secondary" className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Progress Reports:</strong> {analytics.outstandingProgressReports} progress reports are overdue
                </div>
                <Button variant="outline-secondary" size="sm" onClick={() => navigate('/admin/progress-reports?filter=overdue')}>
                  Review Reports
                </Button>
              </Alert>
            )}
          </Col>
        </Row>

        {/* Main Content Tabs */}
        <Tabs defaultActiveKey="overview" id="admin-tabs" className="mb-4">
          <Tab eventKey="overview" title="Overview">
            <Row>
              <Col md={8}>
                <Card>
                  <Card.Header>
                    <h5>Registration Document Statistics</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <small>Registration Document Submission Rate</small>
                            <small>{analytics.documentsSubmitted + analytics.documentsOutstanding > 0 ? Math.round((analytics.documentsSubmitted / (analytics.documentsSubmitted + analytics.documentsOutstanding)) * 100) : 0}%</small>
                          </div>
                          <ProgressBar
                            now={analytics.documentsSubmitted + analytics.documentsOutstanding > 0 ? (analytics.documentsSubmitted / (analytics.documentsSubmitted + analytics.documentsOutstanding)) * 100 : 0}
                            variant="success"
                          />
                          <small className="text-muted">
                            Total expected: {analytics.documentsSubmitted + analytics.documentsOutstanding} documents across all learners
                          </small>
                        </div>
                        <div className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <small>On-Time Submission Rate</small>
                            <small>{analytics.onTimeSubmissionRate}%</small>
                          </div>
                          <ProgressBar
                            now={analytics.onTimeSubmissionRate}
                            variant="info"
                          />
                        </div>
                        <div className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <small>Overall Attendance Rate</small>
                            <small>{analytics.overallAttendance}%</small>
                          </div>
                          <ProgressBar
                            now={analytics.overallAttendance}
                            variant="primary"
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <Table size="sm">
                          <tbody>
                            <tr>
                              <td>Total Learners</td>
                              <td>
                                <Badge bg="primary" className="me-1">{analytics.totalLearners}</Badge>
                                <small className="text-muted">({analytics.activeLearners} active, {analytics.inactiveLearners} inactive)</small>
                              </td>
                            </tr>
                            <tr>
                              <td>Documents Submitted</td>
                              <td><Badge bg="success">{analytics.documentsSubmitted}</Badge></td>
                            </tr>
                            <tr>
                              <td>Outstanding Documents</td>
                              <td><Badge bg="warning">{analytics.documentsOutstanding}</Badge></td>
                            </tr>
                            <tr>
                              <td>Non-Compliant Documents</td>
                              <td><Badge bg="danger">{analytics.nonCompliantDocuments}</Badge></td>
                            </tr>
                            <tr>
                              <td>Outstanding Attendance Registers</td>
                              <td><Badge bg="info">{analytics.outstandingAttendanceRegisters}</Badge></td>
                            </tr>
                            {userProfile?.role === 'admin' && (
                              <tr>
                                <td>Outstanding Progress Reports</td>
                                <td><Badge bg="secondary">{analytics.outstandingProgressReports}</Badge></td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card>
                  <Card.Header>
                    <h5>Recent Activity</h5>
                  </Card.Header>
                  <Card.Body>
                    {recentActivity.length > 0 ? (
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {recentActivity.map((activity, index) => (
                          <div key={index} className="mb-3 pb-2 border-bottom">
                            <div className="d-flex align-items-start">
                              <span className="me-2">{getActivityIcon(activity.type)}</span>
                              <div className="flex-grow-1">
                                <small className="fw-bold">{activity.title}</small>
                                <br />
                                <small className="text-muted">by {activity.user}</small>
                                <br />
                                <small className="text-muted">{activity.time}</small>
                                {activity.priority && (
                                  <Badge bg={activity.priority === 'high' ? 'danger' : 'warning'} className="ms-2">
                                    {activity.priority}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted">No recent activity</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row className="mt-4">
              <Col>
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Signed Attendance Registers</h5>
                    <Button variant="outline-secondary" size="sm" onClick={loadUsersAndCalculateAnalytics}>
                      Refresh
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {signedRegisters.length === 0 ? (
                      <Alert variant="info" className="mb-0">
                        No signed attendance registers found yet.
                      </Alert>
                    ) : (
                      <Table responsive hover size="sm">
                        <thead>
                          <tr>
                            <th>Session</th>
                            <th>Cohort</th>
                            <th>Date</th>
                            <th>Confirmed</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {signedRegisters.slice(0, 10).map((session) => (
                            <tr key={session.sessionId}>
                              <td>{session.title}</td>
                              <td>{session.cohortName}</td>
                              <td>{session.scheduledDate}</td>
                              <td>{session.confirmedCount}</td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => exportSignedRegisterPdf(session.sessionId)}
                                >
                                  Export Signed PDF
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                    {signedRegisters.length > 10 && (
                      <div className="text-muted small">
                        Showing 10 most recent signed registers.
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>
          <Tab eventKey="rsvps" title="Session RSVPs">
            <Row>
              <Col lg={12}>
                <SessionRSVPAdmin />
              </Col>
            </Row>
          </Tab>
          <Tab eventKey="polls" title="Session Poll">
            <Row>
              <Col lg={12}>
                <SessionPollAdmin />
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="province-stats" title="Province Breakdown">
            <Card>
              <Card.Header>
                <h5>Registration Document Status by Province</h5>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Province</th>
                      <th>Total Learners</th>
                      <th>Documents Submitted</th>
                      <th>Outstanding Documents</th>
                      <th>Non-Compliant Documents</th>
                      <th>Submission Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(documentStats.provinceStats || {}).map(([province, stats]) => (
                      <tr key={province}>
                        <td><strong>{province}</strong></td>
                        <td>{stats.total}</td>
                        <td><Badge bg="success">{stats.submitted}</Badge></td>
                        <td><Badge bg="warning">{stats.outstanding}</Badge></td>
                        <td><Badge bg="danger">{stats.nonCompliant || 0}</Badge></td>
                        <td>
                          <ProgressBar
                            now={stats.submitted + stats.outstanding > 0 ? (stats.submitted / (stats.submitted + stats.outstanding)) * 100 : 0}
                            variant="success"
                            label={`${stats.submitted + stats.outstanding > 0 ? Math.round((stats.submitted / (stats.submitted + stats.outstanding)) * 100) : 0}%`}
                            style={{ minWidth: '100px' }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="cohort-stats" title="Cohort Breakdown">
            <Card>
              <Card.Header>
                <h5>Registration Document Status by Cohort</h5>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Cohort</th>
                      <th>Total Learners</th>
                      <th>Documents Submitted</th>
                      <th>Outstanding Documents</th>
                      <th>Non-Compliant Documents</th>
                      <th>Submission Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(documentStats.cohortStats || {}).map(([cohortId, stats]) => {
                      const cohort = cohorts.find(c => c.id === cohortId);
                      return (
                        <tr key={cohortId}>
                          <td><strong>{cohort?.name || 'Unknown Cohort'}</strong></td>
                          <td>{stats.total}</td>
                          <td><Badge bg="success">{stats.submitted}</Badge></td>
                          <td><Badge bg="warning">{stats.outstanding}</Badge></td>
                          <td><Badge bg="danger">{stats.nonCompliant || 0}</Badge></td>
                          <td>
                            <ProgressBar
                              now={stats.submitted + stats.outstanding > 0 ? (stats.submitted / (stats.submitted + stats.outstanding)) * 100 : 0}
                              variant="success"
                              label={`${stats.submitted + stats.outstanding > 0 ? Math.round((stats.submitted / (stats.submitted + stats.outstanding)) * 100) : 0}%`}
                              style={{ minWidth: '100px' }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="users" title="User Management">
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h5>User Management</h5>
                  <Button variant="primary" size="sm" onClick={() => navigate('/admin/users')}>
                    Full User Management
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Enrolled Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.slice(0, 5).map((user) => (
                      <tr key={user.uid || user.id}>
                        <td>{user.displayName}</td>
                        <td>{user.email}</td>
                        <td>
                          <Badge bg={user.role === 'admin' ? 'danger' : user.role === 'facilitator' ? 'warning' : 'primary'}>
                            {user.role}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={user.isActive ? 'success' : 'secondary'}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          {user.createdAt ? 
                            new Date(user.createdAt?.toDate ? user.createdAt.toDate() : user.createdAt).toLocaleDateString() : 
                            'N/A'
                          }
                        </td>
                        <td>
                          <Button variant="outline-primary" size="sm" className="me-1">
                            Edit
                          </Button>
                          <Button variant="outline-info" size="sm">
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="issues" title="Issue Tracker">
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h5>Recent Issues</h5>
                  <Button variant="primary" size="sm" onClick={() => navigate('/admin/issues')}>
                    Full Issue Tracker
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Reporter</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        No issues to display. Issue tracking will be implemented in a future update.
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
        </>
        )}
      </Container>
    </>
  );
};

export default AdminDashboard;
